import { useEffect, useRef, useState } from 'react'
import useStableCallback from './useStableCallback'
import { useStateWithRef } from './useStateWithRef'

export function useQuery<T, P, R = T>({
  queryKey,
  query,
  params,
  transform,
  onSuccess,
  onError,
}: {
  queryKey?: string
  query: (params: P) => Promise<T>
  params?: P | (() => P) | (() => Promise<P>)
  transform?: (data: NonNullable<T>) => R | Promise<R>
  onSuccess?: (response: NonNullable<R>) => void
  onError?: (error: Error) => void
}) {
  const [data, setData] = useState<R | undefined>(undefined)
  const dataRef = useRef<R>()
  const [loading, setLoading, loadingRef] = useStateWithRef(queryKey ? true : false)
  const [error, setError] = useState<string | undefined>(undefined)
  const stableFetchData = useStableCallback(async () => {
    setError(undefined)
    setLoading(true)
    let processedParams: P = params as P
    if (typeof params === 'function') {
      try {
        processedParams = await (params as (() => P) | (() => Promise<P>))()
      } catch (error) {
        setError((error as Error).message)
        setLoading(false)
        setData(undefined)
        return
      }
    }
    await query(processedParams)
      .then(async data => {
        const processedData = transform
          ? await transform(data as NonNullable<T>)
          : (data as unknown as R)
        setError(undefined)
        setLoading(false)
        dataRef.current = processedData
        setData(processedData)
        onSuccess?.(processedData as NonNullable<R>)
      })
      .catch((error: Error) => {
        setError(error.message)
        setLoading(false)
        setData(undefined)
        onError?.(error)
      })
  })
  useEffect(() => {
    if (queryKey) {
      stableFetchData()
    }
  }, [stableFetchData, queryKey])
  return { data, loading, loadingRef, error, executeQuery: stableFetchData, dataRef }
}
